package com.rajen.angrejidictionary.pages

import androidx.compose.runtime.Composable
import com.rajen.angrejidictionary.components.layouts.PageLayoutData
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.OverflowWrap
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@InitRoute
fun initTerms(ctx: InitRouteContext) {
    ctx.data.add(
        PageLayoutData(
            title = "Terms",
            description = "Read the Terms and Conditions for using Angreji Dictionary."
        )
    )
}

@Page("/terms")
@Layout(".components.layouts.PageLayout")
@Composable
fun Terms() {

    // A Box that simply centers its child is robust on every screen size:
    // unlike `margin(left = 50%) + translateX(-50%)`, it never makes the
    // child wider than its container, so it can't trigger horizontal
    // scrolling on narrow viewports.
    Box(
        Modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(900.px),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {

            // Header Section

            SpanText(
                text = "TERMS & CONDITIONS",
                modifier = Modifier
                    .fillMaxWidth()
                    .fontSize(clamp(1.8.cssRem, 5.vw, 2.5.cssRem))
                    .fontWeight(FontWeight.Bold)
                    .textAlign(TextAlign.Center)
                    .color(Color.rgb(0xD3, 0x35, 0x3E))
            )

            SpanText(
                text = "Effective Date: June 1, 2026",
                modifier = Modifier
                    .fillMaxWidth()
                    .margin(top = 0.5.cssRem)
                    .fontSize(1.cssRem)
                    .textAlign(TextAlign.Center)
                    .color(Color.rgb(0xB6, 0x9B, 0x8C))
            )

            Image(
                src = "/divider.png",
                alt = "Divider",
                modifier = Modifier
                    .width(220.px)
                    .margin(topBottom = 1.5.cssRem)
            )

            // Main Content Card

            Column(
                Modifier
                    .fillMaxWidth()
                    .backgroundColor(Color.rgb(0xFF, 0xF7, 0xF1))
                    .borderRadius(28.px)
                    .padding(2.cssRem)
                    .boxShadow(
                        offsetY = 10.px,
                        blurRadius = 30.px,
                        color = Color.rgba(0, 0, 0, 0.06f)
                    ),
                verticalArrangement = Arrangement.spacedBy(1.cssRem)
            ) {

                SpanText(
                    text = "Welcome to Angreji Dictionary. By accessing and using this website or our applications, you agree to comply with and be bound by the following Terms and Conditions.",
                    modifier = Modifier
                        .fontSize(1.05.cssRem)
                        .lineHeight(1.9)
                        .overflowWrap(OverflowWrap.BreakWord)
                        .color(Color.rgb(0x21, 0x00, 0x00))
                        .margin(bottom = 1.cssRem)
                )

                TermsSection(
                    "📖",
                    "Acceptance of Terms",
                    "By accessing or using Angreji Dictionary, you acknowledge that you have read, understood, and agreed to these Terms and Conditions."
                )

                TermsSection(
                    "🌐",
                    "About the Service",
                    "Angreji Dictionary provides English-to-Gujarati word meanings, pronunciations, and educational language resources. The service is offered free of charge and currently does not require account registration."
                )

                TermsSection(
                    "📱",
                    "Service Availability",
                    "Features and functionality may vary between platforms. Certain dictionary features may be available through our Android application, website, or future platforms as determined by us."
                )

                TermsSection(
                    "🔐",
                    "User Responsibilities",
                    "Users agree to use the website lawfully and refrain from any activity that may damage, disrupt, or interfere with the operation of the service."
                )

                TermsSection(
                    "🚫",
                    "Prohibited Activities",
                    "Users may not attempt unauthorized access, distribute malicious software, copy content for commercial purposes, or use the service for unlawful activities."
                )

                TermsSection(
                    "📚",
                    "Intellectual Property",
                    "All content, branding, logos, graphics, definitions, and materials available through Angreji Dictionary are protected by applicable intellectual property laws."
                )

                TermsSection(
                    "🔒",
                    "Privacy",
                    "Your use of Angreji Dictionary is also governed by our Privacy Policy, which explains how information may be collected and used."
                )

                TermsSection(
                    "⚠️",
                    "Disclaimer",
                    "Dictionary definitions, spellings, translations, and examples are provided for educational and informational purposes only. We do not guarantee complete accuracy for every use case."
                )

                TermsSection(
                    "🚫",
                    "Limitation of Liability",
                    "Angreji Dictionary shall not be liable for any direct, indirect, incidental, consequential, or special damages resulting from use of the service."
                )

                TermsSection(
                    "🌍",
                    "Export Compliance",
                    "This service is subject to United States export control laws and regulations. By using our app, you confirm that you are not located in a country under US embargo and that you will comply with all applicable export laws."
                )

                TermsSection(
                    "🔄",
                    "Future Updates",
                    "We may introduce new features, services, applications, or platform support in the future. Features may be modified, enhanced, or discontinued at our discretion."
                )

                TermsSection(
                    "🔄",
                    "Changes to Terms",
                    "We reserve the right to revise these Terms and Conditions at any time. Continued use of the service after changes are published constitutes acceptance of the updated Terms."
                )

                TermsSection(
                    "📜",
                    "Governing Law",
                    "These Terms and Conditions shall be governed by and interpreted in accordance with the laws of India."
                )

                TermsSection(
                    "✉️",
                    "Contact Information",
                    "If you have any questions regarding these Terms and Conditions, please contact us through the Contact page."
                )
            }
        }
    }
}

@Composable
private fun TermsSection(
    icon: String,
    title: String,
    content: String
) {

    Column(
        Modifier
            .fillMaxWidth()
            .backgroundColor(Color.rgb(0xF8, 0xDD, 0xC8))
            .borderRadius(20.px)
            .padding(1.5.cssRem)
            .boxShadow(
                offsetY = 4.px,
                blurRadius = 12.px,
                color = Color.rgba(0, 0, 0, 0.05f)
            )
    ) {

        SpanText(
            text = "$icon  $title",
            modifier = Modifier
                .fontSize(1.2.cssRem)
                .fontWeight(FontWeight.Bold)
                .overflowWrap(OverflowWrap.BreakWord)
                .color(Color.rgb(0xD3, 0x35, 0x3E))
                .margin(bottom = 0.75.cssRem)
        )

        SpanText(
            text = content,
            modifier = Modifier
                .fontSize(1.cssRem)
                .lineHeight(1.8)
                .overflowWrap(OverflowWrap.BreakWord)
                .color(Color.rgb(0x21, 0x00, 0x00))
        )
    }
}